import { LayersThree02, MarkerPin01, NavigationPointer01, Route, Train, Truck01 } from '@untitled-ui/icons-react/build/cjs';

import { AddQuoteKeysType, IAddQuoteStep } from './addQuote.interface';
import { stopStepKey } from './multiStop.helpers';

export const ADD_QUOTE_KEYS: Record<string, AddQuoteKeysType> = {
    SHIPPER_DETAILS: 'shipper_details',
    CONSIGNEE_DETAILS: 'consignee_details',
    SHIPMENT_DETAILS: 'shipment_details',
    DIMENSIONS: 'dimensions',
};

// Client decision (Final wording) — a middle stop's sidebar tab is plain "Stop N" until its
// address is entered, then becomes "Stop N · City, Prov" (e.g. "Stop 1 · Mississauga, ON").
// Falls back to the plain label whenever city is missing, regardless of why (not yet
// entered, or a saved record without one) — never shows a label with a blank city.
const buildStopTabTitle = (t: any, relativeStopNumber: number, stopData: any): string => {
    const city = stopData?.stopAddress?.city;
    const stateCode = stopData?.stopAddress?.stateCode || stopData?.stopAddress?.state;

    if (!city) return t('MyQuote.Stop_N', { n: relativeStopNumber });

    return t('MyQuote.Stop_N_With_Location', { n: relativeStopNumber, city, stateCode: stateCode || '' });
};

// Multi-stop route (stopCount > 2): shipper (sidebar label "Shipper Details", matching the
// Edit Quote modal's convention), N-2 middle StopDetails steps (sidebar label "Stop 1", "Stop 2",
// ... numbered relative to the middle-stop segment, NOT the overall route position — client
// decision), consignee (sidebar label "Consignee Details"), shipment, dimensions. FTL multileg
// ALSO gets Dimensions (CP5) — unlike the legacy 2-stop FTL path below, which has no Dimensions
// step.
const buildMultiStopSteps = (t: any, serviceType: string | null | undefined, stopCount: number, stops: Record<number, any> = {}): IAddQuoteStep[] => {
    const steps: IAddQuoteStep[] = [
        {
            step: 1,
            key: ADD_QUOTE_KEYS.SHIPPER_DETAILS,
            title: t('Common.Shipper_Details'),
            icon: MarkerPin01,
            screenTitle: t('MyQuote.Stop_Origin_Screen_Title'),
            screenDescription: t('MyQuote.Stop_Origin_Screen_Desc'),
        },
    ];

    for (let pos = 2; pos <= stopCount - 1; pos++) {
        // Relative middle-stop number (1-based, independent of overall route position) — pos runs
        // 2..stopCount-1, so pos-1 runs 1..(number of middle stops), matching the sidebar's "Stop
        // 1", "Stop 2", ... regardless of how many total stops the route has.
        const relativeStopNumber = pos - 1;

        steps.push({
            step: pos,
            key: stopStepKey(pos),
            title: buildStopTabTitle(t, relativeStopNumber, stops?.[pos]),
            icon: MarkerPin01,
            screenTitle: t('MyQuote.Stop_Screen_Title', { n: relativeStopNumber }),
            screenDescription: t('MyQuote.Stop_Screen_Desc', { n: relativeStopNumber }),
        });
    }

    steps.push({
        step: stopCount,
        key: ADD_QUOTE_KEYS.CONSIGNEE_DETAILS,
        title: t('Common.Consignee_Details'),
        icon: NavigationPointer01,
        screenTitle: t('MyQuote.Stop_Final_Screen_Title'),
        screenDescription: t('MyQuote.Stop_Final_Screen_Desc'),
    });

    steps.push({
        step: stopCount + 1,
        key: ADD_QUOTE_KEYS.SHIPMENT_DETAILS,
        // Client decision (Final wording) — sidebar tab renamed to "Shipment Overview" regardless
        // of service type. screenTitle (the page's own heading once this step is active) is
        // untouched — still keeps the FTL/LTL-specific wording, only the tab name changed.
        title: t('MyQuote.Tab_Shipment_Overview'),
        icon: Truck01,
        screenTitle: serviceType === 'ftl' ? t('QuotingHub.Common.Load_And_Shipment_Details') : t('QuotingHub.Common.Set_Shipment_Details'),
        screenDescription: serviceType === 'ftl' ? t('MyQuote.Shipment_Details_Screen_Desc_FTL') : t('MyQuote.Shipment_Details_Screen_Desc_LTL'),
    });

    // FTL multileg gets Dimensions too (CP5) — the legacy 2-stop FTL path (below) omits it.
    steps.push({
        step: stopCount + 2,
        key: ADD_QUOTE_KEYS.DIMENSIONS,
        // Client decision (Final wording) — sidebar tab renamed to "Items & Dimensions".
        title: t('MyQuote.Tab_Items_Dimensions'),
        icon: LayersThree02,
        screenTitle: t('MyQuote.Dimensions_Screen_Title'),
        screenDescription: t('MyQuote.Dimensions_Screen_Desc'),
    });

    return steps;
};

export const ADD_QUOTE_STEPS = (t: any, serviceType?: string | null, stopCount: number = 2, stops: Record<number, any> = {}): IAddQuoteStep[] => {
    if (stopCount > 2) {
        return buildMultiStopSteps(t, serviceType, stopCount, stops);
    }

    // ——— legacy body below, byte-identical to the pre-multi-stop implementation ———
    const baseSteps: IAddQuoteStep[] = [
        {
            step: 1,
            key: ADD_QUOTE_KEYS.SHIPPER_DETAILS,
            title: 'Shipper Details',
            icon: MarkerPin01,
            screenTitle: 'Shipper Details',
            screenDescription: 'Enter the shipper address, pickup date and pickup services',
        },
        {
            step: 2,
            key: ADD_QUOTE_KEYS.CONSIGNEE_DETAILS,
            title: 'Consignee Details',
            icon: NavigationPointer01,
            screenTitle: 'Consignee Details',
            screenDescription: 'Enter the consignee address, delivery date and delivery services',
        },
    ];

    // FTL: 3 steps only (no Dimensions step)
    if (serviceType === 'ftl') {
        return [
            ...baseSteps,
            {
                step: 3,
                key: ADD_QUOTE_KEYS.SHIPMENT_DETAILS,
                // Client decision (Final wording) — sidebar tab renamed; screenTitle (page heading) unchanged.
                title: t('MyQuote.Tab_Shipment_Overview'),
                icon: Truck01,
                screenTitle: 'Load & Shipment Details',
                screenDescription: 'Add load overview, equipment type, services and insurance',
            },
        ];
    }

    // LTL (and default): 4 steps with Dimensions
    return [
        ...baseSteps,
        {
            step: 3,
            key: ADD_QUOTE_KEYS.SHIPMENT_DETAILS,
            // Client decision (Final wording) — sidebar tab renamed; screenTitle (page heading) unchanged.
            title: t('MyQuote.Tab_Shipment_Overview'),
            icon: Truck01,
            screenTitle: 'Set Shipment Details',
            screenDescription: 'Add essential shipment information for processing',
        },
        {
            step: 4,
            key: ADD_QUOTE_KEYS.DIMENSIONS,
            // Client decision (Final wording) — sidebar tab renamed; screenTitle (page heading) unchanged.
            title: t('MyQuote.Tab_Items_Dimensions'),
            icon: LayersThree02,
            screenTitle: 'Dimensions & Visualizations',
            screenDescription: 'Provide package dimensions to calculate volume and weight',
        },
    ];
};

export const INTER_MODAL_TYPE_OPTIONS = [
    { label: 'Both', value: 'both', icon: Route },
    { label: 'Road', value: 'road', icon: Truck01 },
    { label: 'Rail', value: 'rail', icon: Train },
];
